import { readSearchReturn } from './hubSearch';
import type { HubOccasion } from './hubDocs';

export type HubTab = 'people' | 'calendar' | 'guides' | 'you';
export interface HubCrumb { label: string; href: string; state?: unknown }
export interface HubNavigation { active: HubTab; crumbs: HubCrumb[] }

export const normalizeHubPath = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

const hub = { label: 'Gift guides', href: '/gift-guides' };
const calendar = { label: 'Calendar', href: '/calendar' };
const people = { label: 'People', href: '/' };

/** Content ancestry never writes history. State describes an explicit entry,
 * not a synthetic stack of pages a deep-link visitor has never visited. */
export function resolveHubNavigation(
  pathname: string,
  state: unknown,
  occasions: readonly HubOccasion[] = [],
  search = '',
): HubNavigation {
  pathname = normalizeHubPath(pathname);
  const entry = state && typeof state === 'object' ? state as Record<string, unknown> : {};
  if (pathname === '/holidays') {
    const origin = entry.hubEntry ?? new URLSearchParams(search).get('from');
    return { active: origin === 'calendar' ? 'calendar' : 'you', crumbs: [] };
  }
  const guide = pathname.startsWith('/guide/');
  const collection = pathname.startsWith('/gift-guides/');
  if (guide || collection) {
    const from = entry.from && typeof entry.from === 'object' ? entry.from as Record<string, unknown> : {};
    // Preserve a board's one-tap return, in the shortened PRD form. State is
    // untrusted: only local paths, and never a protocol-relative/backslash URL.
    if (typeof from.recipientName === 'string' && from.recipientName.trim() &&
        typeof from.boardPath === 'string' && /^\/(?!\/)/.test(from.boardPath) && !/[\\\r\n]/.test(from.boardPath)) {
      return { active: 'people', crumbs: [{ label: from.recipientName, href: from.boardPath }] };
    }
    const results = readSearchReturn(entry);
    if (guide && results) return { active: 'guides', crumbs: [{ label: 'Results', href: results.href, state: { hubSearchScroll: results.scrollY } }] };
    // A tab origin: the Calendar, or People's Coming up strip (THE-1403).
    // Without an explicit collection hop the crumb is that tab itself, never
    // an inferred ancestor — a holiday whose collection holds one guide skips
    // that collection, so a crumb back to it would invent a page never seen.
    const origin = entry.hubEntry === 'calendar' ? 'calendar'
      : entry.hubEntry === 'people' ? 'people' : undefined;
    const explicit = occasions.find(o => o.key === entry.hubCollection);
    const ancestor = explicit ?? occasions.find(o => o.guideSlugs.includes(pathname.slice('/guide/'.length)));
    // The nearer collection wins over the origin tab on its child guide, while
    // the tab origin survives. Its link hands that origin back to the collection.
    if (origin) return { active: origin, crumbs: guide && explicit
      ? [{ label: explicit.label, href: `/gift-guides/${explicit.key}`, state: { hubEntry: origin } }]
      : [origin === 'calendar' ? calendar : people] };
    if (guide && entry.hubEntry === 'hub') return { active: 'guides', crumbs: [hub] };
    if (guide && typeof entry.fromHub === 'string' && !entry.hubCollection) {
      return { active: 'guides', crumbs: [hub] };
    }
    return { active: 'guides', crumbs: guide && ancestor
      ? [{ label: ancestor.label, href: `/gift-guides/${ancestor.key}` }]
      : [hub] };
  }
  // THE-1187 QA round 4 (shepherd, 2026-10-26): the per-person Saved page
  // (`/liked/:recipientId`) used to fall into the bare `/liked` branch below
  // (`you`), but the desktop nav never renders a `you` tab (it's filtered
  // out — mobile-only, see `HubNavigationView`), so nothing read as active
  // there — Kate's mock shows People selected. Board pages (`/board`,
  // `/b/:id`) match no prefix here and land on the `people` default for the
  // same reason (THE-889); `pathname === '/liked'` (exact) keeps the
  // all-saves board on `you` while every `/liked/:id` drill-down now falls
  // through to that same `people` default.
  return { active: (pathname.startsWith('/calendar') || pathname.startsWith('/holidays')) ? 'calendar'
    : pathname.startsWith('/gift-guides') ? 'guides'
    : pathname.startsWith('/settings') || pathname.startsWith('/you') || pathname === '/liked' ? 'you' : 'people', crumbs: [] };
}

/** Calendar and liked pages own an inner scroll box. Scroll that box as well
 * as the document; do not navigate to the same URL and grow browser history. */
export function scrollHubTabToTop() {
  document.querySelectorAll<HTMLElement>('[data-calendar-scroller], [data-hub-scroll-root]')
    .forEach(node => node.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }));
  window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
}
